import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, MailCheck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useSearchParams } from 'react-router';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { withNodes } from '@/features/billing/lib/richText';
import { useT } from '@/lib/i18n';

import { resendVerificationCode, verifyEmail } from '../api/auth';
import { EmailCodeForm } from '../components/EmailCodeForm';
import { useFinishSignup } from '../hooks/useFinishSignup';
import { clearPendingSignup, takePendingSignup } from '../lib/pendingSignup';

function emailSchema(t: ReturnType<typeof useT>) {
  return z.object({
    email: z.string().min(1, t('auth.common.email_required')).email(t('auth.common.email_invalid')),
  });
}

type FormValues = z.infer<ReturnType<typeof emailSchema>>;
type State = 'idle' | 'verifying' | 'verified' | 'failed';

/** What the register page hands over when it navigates here. */
interface SignupHandOff {
  fromSignup?: boolean;
  resendAfter?: number;
}

/**
 * Where an account proves its address - straight after registering, or later.
 *
 * Three ways in, and the URL says which:
 *
 * - `?token=` is a link out of an email - what a WhatsApp signup is sent, and
 *   what older emails still carry. It is spent on arrival without asking.
 * - `?email=` is the register page handing over: the account exists, a code is
 *   on its way, and this opens on the code box. If the register page also left
 *   the password in memory, an accepted code signs the merchant straight in and
 *   carries on to their business and plan; after a reload it cannot, and they
 *   are asked to sign in instead.
 * - Neither is somebody who came back later: their address, then the code.
 */
export function VerifyEmailPage() {
  const t = useT();
  const schema = useMemo(() => emailSchema(t), [t]);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const finishSignup = useFinishSignup();
  const token = searchParams.get('token');
  const handedOver = searchParams.get('email');
  const handOff = (location.state ?? {}) as SignupHandOff;

  const [state, setState] = useState<State>(token ? 'verifying' : 'idle');
  const [error, setError] = useState<string | null>(null);
  // An error from the step *after* the code - signing in - which only a link
  // to the sign-in page can resolve, so it is shown differently.
  const [signInError, setSignInError] = useState<string | null>(null);
  // The address a code has been sent to, which is what turns this screen from
  // "who are you" into "type the six digits".
  const [sentTo, setSentTo] = useState<{ email: string; resendAfter: number } | null>(() =>
    handedOver && !token ? { email: handedOver, resendAfter: handOff.resendAfter ?? 0 } : null,
  );
  // React 18+ StrictMode mounts effects twice in development; verification
  // tokens are single-use, so a second call would report "already used".
  const attempted = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;

    void (async () => {
      try {
        await verifyEmail(token);
        setState('verified');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('auth.verify.link_failed'));
        setState('failed');
      }
    })();
  }, [token]);

  const onSend = async (values: FormValues) => {
    setError(null);
    try {
      const sent = await resendVerificationCode(values.email);
      setSentTo({ email: values.email, resendAfter: sent.resend_after });
    } catch (err) {
      // Includes the cooldown: a code is already in flight, and the next
      // screen is where they should be either way.
      if (err instanceof ApiError && err.code === 'CODE_RESEND_TOO_SOON') {
        const retry = typeof err.details?.retry_after === 'number' ? err.details.retry_after : 0;
        setSentTo({ email: values.email, resendAfter: retry });
        return;
      }
      setError(err instanceof ApiError ? err.message : t('auth.verify.send_failed'));
    }
  };

  const onVerified = async (email: string) => {
    const password = takePendingSignup(email);
    if (password === null) {
      // Verified, but nothing to sign in with - a reload lost the hand-over,
      // or they came here on their own. Their password still works.
      setState('verified');
      return;
    }
    setSignInError(await finishSignup(email, password));
  };

  if (state === 'verifying') {
    return <p className="text-center text-sm text-muted">{t('auth.verify.verifying')}</p>;
  }

  if (state === 'verified') {
    return (
      <div className="text-center">
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-brand-50">
          <CheckCircle2 className="size-6 text-brand-600" aria-hidden />
        </div>
        <h1 className="mt-4 text-2xl font-bold tracking-tight text-ink">{t('auth.verify.verified_title')}</h1>
        <p className="mt-2 text-sm text-muted">{t('auth.verify.verified_body')}</p>
        <Link
          to="/login"
          className="mt-6 inline-flex h-11 items-center rounded-xl bg-brand-500 px-6 text-sm font-semibold text-white hover:bg-brand-600"
        >
          {t('auth.common.sign_in')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-brand-50">
        <MailCheck className="size-6 text-brand-600" aria-hidden />
      </div>
      <h1 className="mt-4 text-center text-2xl font-bold tracking-tight text-ink">
        {sentTo && handOff.fromSignup ? t('auth.verify.title_check') : t('auth.verify.title_verify')}
      </h1>

      {state === 'failed' && error ? (
        <Alert tone="error" className="mt-4">
          {error}
        </Alert>
      ) : null}

      {sentTo ? (
        <div className="mt-5">
          <p className="mb-4 text-center text-sm text-muted">
            {withNodes(
              t(handOff.fromSignup ? 'auth.verify.sent_signup' : 'auth.verify.sent_later'),
              { email: <strong className="text-ink">{sentTo.email}</strong> },
            )}
          </p>

          {signInError ? (
            // Only reachable once the address is already verified, because the
            // only step after it that can fail is the sign-in. The link is the
            // whole point: without it this screen still asks for a code that
            // has already been spent, and there is no way on.
            <Alert tone="error" className="mb-4">
              {signInError}{' '}
              <Link to="/login" className="font-semibold underline">
                {t('auth.common.sign_in')}
              </Link>
            </Alert>
          ) : null}

          <EmailCodeForm
            email={sentTo.email}
            resendAfter={sentTo.resendAfter}
            onVerified={() => onVerified(sentTo.email)}
          />
          <p className="mt-6 text-center text-sm text-muted">
            {t('auth.verify.wrong_address')}{' '}
            {handOff.fromSignup ? (
              // The account exists under the address they typed, so the form
              // they came from would only answer "already registered". Signing
              // up again with the right one is the way out.
              <Link
                to="/register"
                onClick={() => clearPendingSignup()}
                className="font-semibold text-brand-600 hover:underline"
              >
                {t('auth.verify.start_again')}
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setSentTo(null)}
                className="font-semibold text-brand-600 hover:underline"
              >
                {t('auth.verify.go_back')}
              </button>
            )}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSend)} noValidate className="mt-5 space-y-4">
          {state !== 'failed' && error ? <Alert tone="error">{error}</Alert> : null}
          <p className="text-sm text-muted">
            {t('auth.verify.intro')}
          </p>
          <TextField
            label={t('auth.common.email')}
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />
          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('auth.common.sending') : t('auth.verify.submit')}
          </Button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-muted">
        <Link to="/login" className="font-semibold text-brand-600 hover:underline">
          {t('auth.common.back_to_sign_in')}
        </Link>
      </p>
    </div>
  );
}
